import { FRAGMENTS_DIRECTORY, GRAMAX_DIRECTORY } from "@app/config/const";
import Path from "@core/FileProvider/Path/Path";
import type { Article, Content } from "@core/FileStructue/Article/Article";
import type { ReadonlyCatalog } from "@core/FileStructue/Catalog/ReadonlyCatalog";
import ArticleProvider from "@ext/articleProvider/logic/ArticleProvider";
import type { ResolvedArticle, ScopeNotReady } from "@ext/articleProvider/logic/ArticleScopes";
import type ParserContext from "@ext/markdown/core/Parser/ParserContext/ParserContext";
import type PrivateParserContext from "@ext/markdown/core/Parser/ParserContext/PrivateParserContext";
import type { Tag } from "@ext/markdown/core/render/logic/Markdoc";
import type {
	FragmentError,
	FragmentReference,
	FragmentRenderData,
} from "@ext/markdown/elements/fragment/edit/model/types";
import getFragmentScope from "@ext/markdown/elements/fragment/logic/getFragmentScope";

export const parseFragment = async (fragment: Article, context: ParserContext): Promise<Content> => {
	const markCurrent = fragment.beginParse();
	const cached = fragment.parsedContent.peek();
	if (cached && !fragment.isParseStale()) {
		for (const name of context.getCatalog().articleScopes.dependenciesOf(fragment)) context.dependOnScope(name);
		return cached;
	}

	const parse = async () => context.parser.parse(await fragment.getContent(), context);

	let content: Content;
	const underLock = await fragment.parsedContent.tryWrite(async () => {
		content = await parse();
		markCurrent();
		return content;
	});
	// The lock is held by a parse that may itself be waiting on this one: a private copy, never cached, instead.
	return underLock ? content : await parse();
};

export const getFragmentRenderData = async (
	id: string,
	fragment: Article,
	context: ParserContext,
): Promise<FragmentRenderData> => {
	const content = await parseFragment(fragment, context);

	return {
		id,
		title: fragment.getTitle(),
		content: (content.renderTree as Tag).children,
		path: fragment.ref.path.value,
	};
};

/** A scope that has not loaded its content may still hold the fragment. */
const firstNotReady = async (
	scopes: ReadonlyCatalog["articleScopes"],
	names: string[],
): Promise<ScopeNotReady | null> => {
	for (const name of names) {
		const notReady = await scopes.get(name).notReady?.();
		if (notReady) return notReady;
	}
	return null;
};

const notFound = async (
	catalog: ReadonlyCatalog,
	id: string,
	scope: string | undefined,
	unavailable: string[],
): Promise<FragmentError> => {
	const scopes = catalog.articleScopes;
	const titlesOf = (names: string[]) => names.map((name) => ({ name, title: scopes.get(name).title }));

	if (unavailable.length) return { reason: "unavailable", id, scopes: titlesOf(unavailable) };
	if (scope && !scopes.get(scope)) return { reason: "unknown-scope", id, scope };

	const searched = scope ? [scope] : scopes.names();
	const notReady = await firstNotReady(scopes, searched);
	if (notReady) return { reason: "scope-not-ready", id, state: notReady.state, text: notReady.text };

	if (scope) return { reason: "not-found-in-scopes", id, scopes: titlesOf(searched) };

	const path = new Path([GRAMAX_DIRECTORY, FRAGMENTS_DIRECTORY, `${id}.md`]).value;
	if (searched.length) return { reason: "not-found-in-scopes", id, scopes: titlesOf(searched), path };

	return { reason: "not-found", id, path };
};

const pathInOwner = ({ article, owner }: ResolvedArticle) =>
	(owner.basePath.subDirectory(article.ref.path) ?? article.ref.path).value;

const failed = (id: string, error?: FragmentError): FragmentRenderData => ({ id, title: null, content: null, error });

export const missingFragment = async (
	catalog: ReadonlyCatalog,
	id: string,
	scope: string | undefined,
	unavailable: string[],
): Promise<FragmentRenderData> => failed(id, await notFound(catalog, id, scope, unavailable));

export const resolveFragment = async (
	context: PrivateParserContext,
	{ id, type }: FragmentReference,
): Promise<FragmentRenderData> => {
	const scope = getFragmentScope(type);
	let resolved: ResolvedArticle | null = null;

	if (!id) return failed(id);
	const catalog = context.getCatalog();

	try {
		const lookup = await ArticleProvider.lookup(catalog, "fragment", id, scope, (name) =>
			context.dependOnScope(name),
		);
		resolved = lookup.resolved;
		if (!resolved) return await missingFragment(catalog, id, scope, lookup.unavailable);
		if (context.getEmbeddingChain().includes(resolved.article.ref.path.value))
			return failed(id, { reason: "cycle", id, path: pathInOwner(resolved) });

		const fragmentContext = context.createContext(resolved.article, resolved.owner);
		const data = await getFragmentRenderData(id, resolved.article, fragmentContext);
		if (!scope) context.fragment.add(id);
		return { ...data, owner: resolved.owner.name, foreign: resolved.owner !== catalog };
	} catch {
		return failed(
			id,
			resolved ? { reason: "failed", path: pathInOwner(resolved) } : { reason: "unavailable", id, scopes: [] },
		);
	}
};
